import { zodResolver } from "@hookform/resolvers/zod"
import { Controller, useForm } from "react-hook-form"
import { useNavigate } from "react-router"
import { toast } from "sonner"
import z from "zod"

import DateTimePicker from "@/components/DateTimePicker"
import TimezoneSelector from "@/components/TimezoneSelector"
import { Button } from "@/components/ui/button"
import {
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field"
import { Input } from "@/components/ui/input"

import { runReport } from "../../api/report/runReport"

import type { ConstantReportRange, DataPointReport } from "../../types/reportTypes"

const formSchema = z.object({
  start: z.date(),
  end: z.date(),
  interval: z.int().min(1, "Interval must be at least 1 second"),
  timezone: z.string(),
  decimals: z.int().min(0),
})

export default function ReportRunDialog({ report }: { report: DataPointReport }) {
  const navigate = useNavigate()
  const range = report.range as ConstantReportRange

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      start: new Date(range.start),
      end: new Date(range.end),
      interval: report.interval,
      timezone: "UTC",
      decimals: 2,
    },
  })

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    await runReport(
      report.id || 0,
      data.start,
      data.end,
      data.interval,
      data.timezone,
      data.decimals,
    )
    toast("Report run created")
    navigate("/datapoints/report-runs")
  }

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Run Report</DialogTitle>
      </DialogHeader>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FieldGroup className="gap-3">
          <Controller
            name="start"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel>Start Time</FieldLabel>
                <DateTimePicker value={field.value} onChange={field.onChange} />
                <FieldDescription>In local timezone</FieldDescription>
                {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
              </Field>
            )}
          />
          <Controller
            name="end"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel>End Time</FieldLabel>
                <DateTimePicker value={field.value} onChange={field.onChange} />
                <FieldDescription>In local timezone</FieldDescription>
                {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
              </Field>
            )}
          />
          <Controller
            name="interval"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel>Interval</FieldLabel>
                <Input
                  type="number"
                  step={1}
                  value={field.value}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                />
                <FieldDescription>In seconds</FieldDescription>
                {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
              </Field>
            )}
          />
          <Controller
            name="timezone"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel>Timezone</FieldLabel>
                <TimezoneSelector {...field} />
                <FieldDescription>Display timezone for timestamps</FieldDescription>
                {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
              </Field>
            )}
          />
          <Controller
            name="decimals"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel>Decimal Places</FieldLabel>
                <Input
                  type="number"
                  step={1}
                  value={field.value}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                />
                <FieldDescription>Places to round values to</FieldDescription>
                {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
              </Field>
            )}
          />
        </FieldGroup>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button type="submit">Run Report</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  )
}
